import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { footerNavigation, navigation, routes } from "../../data/site";
import { cx } from "../../lib/cx";
import { localHref } from "../../lib/links";
import { ButtonLink } from "../ui/Button";
import Logo from "./Logo";

/** Nav label for the current path ("/case-studies/2/" → "Results"). */
const activeSection = (path) =>
  footerNavigation.find(
    ({ href }) => href !== routes.home && path.startsWith(href),
  )?.label;

// No backdrop-filter on the header: it would make the fixed mobile menu
// position against the header instead of the viewport.
/**
 * Sticky paper-white bar with the Golden Sun top rule, the wordmark and its
 * "Construction Technology" tag, the main links and Book a Meeting.
 */
export default function SiteHeader({ path }) {
  const [open, setOpen] = useState(false);
  const active = activeSection(path);

  useEffect(() => setOpen(false), [path]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  return (
    <header className="sticky top-0 z-50 px-4 sm:px-6 border-t-[3px] border-b border-t-yellow border-b-line bg-surface shadow-[0_1px_6px_rgba(0,0,0,0.04)]">
      <div className="mx-auto flex h-[68px] max-w-280 items-center gap-5">
        <div className="flex items-center gap-3.5">
          <Logo aria-label="Xeratec home" />
          <span className="tag hidden border-l-2 border-line-strong pl-3.5 text-[11px] leading-[1.3] font-semibold tracking-[0.2em] text-muted xl:block">
            Construction
            <br />
            Technology
          </span>
        </div>
        <nav
          aria-label="Main navigation"
          className="ml-auto hidden items-center gap-7 lg:flex"
        >
          {navigation.map(({ label, href }) => (
            <a
              key={href}
              href={localHref(href)}
              aria-current={active === label ? "page" : undefined}
              className={cx(
                "relative py-1 text-[15px] font-bold transition-colors hover:text-blue-deep",
                active === label
                  ? "text-blue-deep after:absolute after:inset-x-0 after:-bottom-0.5 after:h-0.5 after:bg-yellow"
                  : "text-slate",
              )}
            >
              {label}
            </a>
          ))}
        </nav>
        <ButtonLink size="md" className="ml-auto max-sm:hidden lg:ml-2" />
        <button
          className="-mr-2 ml-auto grid size-11 place-items-center rounded-brand text-slate sm:ml-1 lg:hidden"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-navigation"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>
      {open && (
        <nav
          id="mobile-navigation"
          aria-label="Mobile navigation"
          className="blueprint fixed inset-x-0 top-[71px] bottom-0 flex flex-col overflow-y-auto px-6 pt-4 pb-10 text-white lg:hidden"
        >
          {footerNavigation.map(({ label, href }) => (
            <a
              key={href}
              href={localHref(href)}
              onClick={() => setOpen(false)}
              aria-current={active === label ? "page" : undefined}
              className={cx(
                "font-display border-b border-white/10 py-4 text-3xl uppercase",
                active === label && "text-yellow",
              )}
            >
              {label}
            </a>
          ))}
          <ButtonLink className="mt-8 self-start" />
        </nav>
      )}
    </header>
  );
}
